import { useEffect, useRef } from "react";
import { Camera, FolderOpen, ImageIcon, ImagePlus, Loader2, Send, Square, X } from "lucide-react";

/**
 * Savol maydoni.
 *
 * `textarea` — savol uzun bo'lishi mumkin (tovar tavsifi bir necha
 * qator). Enter yuboradi, Shift+Enter yangi qator.
 *
 * Foto biriktirilsa so'rov tasvirga ketadi, matn esa tovar nomi bo'ladi.
 */
export function Composer({
  value, onChange, onSubmit, onStop, busy,
  photos = [], preparing = 0, maxPhotos = 3, onAddPhotos, onRemovePhoto,
  menuOpen = false, onMenu, onCamera, onFiles,
}) {
  const ref = useRef(null);
  const menuRef = useRef(null);
  const ready = (value.trim() || photos.length > 0) && !busy && !preparing;

  // Telefonda fokus klaviaturani ochib, fotoni yopib qo'yadi
  useEffect(() => {
    if (photos.length && !window.matchMedia("(pointer: coarse)").matches) ref.current?.focus();
  }, [photos.length]);

  useEffect(() => {
    if (!menuOpen) return;
    const away = (e) => !menuRef.current?.contains(e.target) && onMenu(false);
    const esc = (e) => e.key === "Escape" && onMenu(false);
    document.addEventListener("pointerdown", away);
    window.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [menuOpen, onMenu]);

  const pick = (fn) => {
    onMenu(false);
    fn();
  };

  useEffect(() => {
    ref.current?.focus();
  }, []);

  // Balandlik matnga moslashadi: bir qatordan 160px gacha.
  //
  // O'lchov `rAF` ichida: effekt joylashuv hisoblanmasdan oldin
  // ishlaydi va o'shanda `scrollHeight` chegarani (160px) qaytaradi —
  // maydon bo'sh holida to'rt barobar baland bo'lib qotib qolardi.
  // Shrift kechroq yuklansa o'lchov o'zgaradi, `fonts.ready` shuning
  // uchun.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      el.style.height = "0px";
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    };

    const id = requestAnimationFrame(fit);
    document.fonts?.ready.then(fit);
    return () => cancelAnimationFrame(id);
  }, [value]);

  const submit = (e) => {
    e.preventDefault();
    if (ready) onSubmit();
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 pb-3 pt-1 sm:px-6">
      <form
        onSubmit={submit}
        className="relative w-full min-w-0 rounded-2xl border border-sky-500/30 bg-[#061c3b]/95 p-1.5 shadow-lg shadow-sky-950/50 backdrop-blur-md transition-all focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-400/20 sm:p-2"
      >
        {photos.length + preparing > 0 && (
          <div className="flex gap-2 px-1.5 pb-1.5 pt-1">
            {photos.map((p, i) => (
              <div key={p.thumb || i} className="relative">
                {p.thumb ? (
                  <img src={p.thumb} alt="" className="h-16 w-16 rounded-xl object-cover sm:h-20 sm:w-20" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-sky-950/80 text-sky-400/60 sm:h-20 sm:w-20">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemovePhoto(i)}
                  title="Olib tashlash"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-900/90 p-1 text-white ring-1 ring-white/30 hover:bg-rose-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {Array.from({ length: preparing }, (_, i) => (
              <div
                key={`p${i}`}
                className="flex h-16 w-16 items-center justify-center rounded-xl bg-sky-950/80 sm:h-20 sm:w-20"
              >
                <Loader2 className="h-5 w-5 animate-spin text-sky-300" />
              </div>
            ))}
          </div>
        )}

        <div className="flex min-w-0 items-end gap-2">
          <div ref={menuRef} className="relative shrink-0 self-center">
            <button
              type="button"
              onClick={() => onMenu(!menuOpen)}
              disabled={photos.length + preparing >= maxPhotos}
              title="Foto qo'shish"
              className={`rounded-xl p-2 transition-colors disabled:opacity-30 ${
                menuOpen ? "bg-sky-800/50 text-white" : "text-sky-300 hover:bg-sky-800/40 hover:text-white"
              }`}
            >
              <ImagePlus className="h-5 w-5" />
            </button>

            {menuOpen && (
              <div className="absolute bottom-full left-0 z-40 mb-3 w-72 rounded-2xl border border-sky-800/60 bg-[#051329] p-1.5 shadow-2xl shadow-black/50">
                {[
                  [Camera, "Kamera", "Suratga olish", onCamera],
                  [FolderOpen, "Fayldan", "Galereya yoki papkadan", onFiles],
                ].map(([Icon, label, hint, fn]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => pick(fn)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-sky-900/50"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-sky-300" />
                    <span className="text-sm font-medium text-white">{label}</span>
                    <span className="truncate text-xs text-sky-300/50">{hint}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <textarea
            ref={ref}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) submit(e);
            }}
            onPaste={(e) => {
              const files = [...e.clipboardData.files].filter((f) => f.type.startsWith("image/"));
              if (!files.length) return;
              e.preventDefault();
              onAddPhotos(files);
            }}
            placeholder={photos.length ? "Tovar nomi (ixtiyoriy)" : "Tovarni yozing yoki foto yuklang..."}
            className="min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-xs leading-6 text-white caret-sky-400 placeholder:text-sky-300/40 focus:outline-none sm:text-sm"
          />

          {value.length > 0 && !busy && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                ref.current?.focus();
              }}
              title="Tozalash"
              className="shrink-0 self-center rounded-full p-1 text-sky-400/50 transition-colors hover:bg-sky-800/40 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {busy ? (
            /*
              To'xtatish — YUBORISH emas, shuning uchun ovozi past:
              konturli, to'ldirilmagan. To'q qizil fon interfeysdagi eng
              baland element bo'lib, ko'z uni xato deb o'qirdi — holbuki
              bu shunchaki bekor qilish.

              O'lcham "Yuborish" bilan bir xil (`px-4 py-2 sm:px-6
              sm:py-2.5`): tugma almashganda maydon eni sakramaydi.
            */
            <button
              type="button"
              onClick={onStop}
              title="To'xtatish"
              className="flex shrink-0 items-center justify-center gap-1.5 self-center rounded-xl border border-sky-700/50 bg-sky-950/60 px-4 py-2 text-xs font-medium tracking-wide text-sky-200 transition-colors hover:border-rose-500/50 hover:bg-rose-950/40 hover:text-rose-200 sm:px-6 sm:py-2.5 sm:text-sm"
            >
              <Square className="h-3 w-3 fill-current" />
              <span className="hidden sm:inline">To'xtatish</span>
            </button>
          ) : (
            <button
              type="submit"
              disabled={!ready}
              className={`flex shrink-0 select-none items-center justify-center gap-1.5 self-center rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all sm:px-6 sm:py-2.5 sm:text-sm ${
                ready
                  ? "bg-sky-600 text-white shadow-md shadow-sky-600/30 hover:bg-sky-500 active:scale-[0.98]"
                  : "cursor-not-allowed border border-sky-800/30 bg-sky-950/60 text-sky-400/30"
              }`}
            >
              <span className="hidden sm:inline">Yuborish</span>
              <Send className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </form>

      <p className="mt-1.5 px-2 text-center text-[10px] text-sky-400/40">
        Javoblar rasmiy TIF TN ma'lumotlariga asoslanadi
      </p>
    </div>
  );
}
